/**
 * Shared helpers for the Comments feature: mention (de)serialization, thread
 * tree building, and small display utilities. Kept framework-agnostic so both
 * the editor and the read-only renderer can use them.
 */

/** Backend mention token: @[<24-char hex staff ObjectId>]. */
export const MENTION_REGEX = /@\[([a-f\d]{24})\]/gi;

/**
 * Serialize a TipTap doc (editor.getJSON()) to the plain-text format the
 * comment backend expects: mention nodes become `@[staffId]`, paragraphs are
 * separated by newlines. We serialize from JSON (not editor.getText()) so the
 * mention token shape is deterministic regardless of TipTap version.
 */
export const serializeDocToText = (doc) => {
    const walk = (node) => {
        if (!node) return '';
        if (node.type === 'text') return node.text || '';
        if (node.type === 'mention') return `@[${node.attrs?.id || ''}]`;
        if (node.type === 'hardBreak') return '\n';
        const inner = (node.content || []).map(walk).join('');
        if (node.type === 'paragraph') return `${inner}\n`;
        return inner;
    };
    const text = (doc?.content || []).map(walk).join('');
    return text.replace(/\n+$/g, '').trim();
};

/**
 * Split a stored comment `text` into renderable segments. Mention tokens are
 * resolved to display names via the comment's populated `mentions` array
 * ([{ id, firstName, lastName }]). Returns [{ type: 'text'|'mention', ... }].
 */
export const parseCommentText = (text, mentions = []) => {
    if (!text) return [];
    const nameById = {};
    (mentions || []).forEach((m) => {
        nameById[m.id] = `${m.firstName || ''} ${m.lastName || ''}`.trim() || 'Unknown';
    });
    const segments = [];
    let lastIndex = 0;
    let match;
    const regex = new RegExp(MENTION_REGEX);
    while ((match = regex.exec(text)) !== null) {
        if (match.index > lastIndex) {
            segments.push({ type: 'text', value: text.slice(lastIndex, match.index) });
        }
        const id = match[1];
        segments.push({ type: 'mention', id, value: `@${nameById[id] || 'Unknown'}` });
        lastIndex = regex.lastIndex;
    }
    if (lastIndex < text.length) {
        segments.push({ type: 'text', value: text.slice(lastIndex) });
    }
    return segments;
};

/**
 * Turn a flat comment list (parentComment = null for roots) into a nested
 * tree: [{ ...comment, replies: [...] }]. Each level is sorted oldest-first.
 */
export const buildCommentTree = (list = []) => {
    const byParent = {};
    list.forEach((c) => {
        const key = c.parentComment || 'root';
        (byParent[key] = byParent[key] || []).push(c);
    });
    const sortByDate = (arr) => arr.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    const attach = (parentId) =>
        sortByDate(byParent[parentId] || []).map((c) => ({ ...c, replies: attach(c.id) }));
    return attach('root');
};

const escapeHtml = (s = '') => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * Rebuild editor-ready HTML from a stored comment `text`, so an edit re-hydrates
 * mention chips (not raw `@[id]` tokens). Mentions become TipTap mention spans
 * the Mention extension parses back into nodes; newlines become paragraphs.
 */
export const textToMentionHtml = (text, mentions = []) => {
    const segments = parseCommentText(text, mentions);
    const inline = segments
        .map((seg) =>
            seg.type === 'mention'
                ? `<span data-type="mention" data-id="${seg.id}" data-label="${escapeHtml(seg.value.replace(/^@/, ''))}">${escapeHtml(seg.value)}</span>`
                : escapeHtml(seg.value),
        )
        .join('');
    const paragraphs = inline
        .split('\n')
        .map((line) => `<p>${line || '<br>'}</p>`)
        .join('');
    return paragraphs || '<p></p>';
};

/** Full display name from a { firstName, lastName } author-ish object. */
export const fullName = (person) => `${person?.firstName || ''} ${person?.lastName || ''}`.trim();

/** Two-letter initials for an avatar bubble. */
export const initialsOf = (person) => {
    const name = fullName(person);
    return (
        name
            .split(' ')
            .map((n) => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2) || '?'
    );
};

/** Deterministic avatar background from an id, so a person keeps one color. */
const AVATAR_COLORS = ['#6558F5', '#F5A623', '#EB5757', '#2D9CDB', '#27AE60', '#9B51E0', '#EB5E9C', '#00A5A5'];
export const avatarColor = (id = '') => {
    let sum = 0;
    for (let i = 0; i < id.length; i++) sum += id.charCodeAt(i);
    return AVATAR_COLORS[sum % AVATAR_COLORS.length];
};
